/**
 * SnowEffect.ts - WebGL Snow Particles with Ground Accumulation
 * Uses TypedArrays for high-performance particle management
 */

import { BaseEffect } from './BaseEffect';
import type { AtmosphereConfig } from '../core/StateManager';

// Inline shaders to avoid async loading
// Wind is now handled in CPU update() for realistic cumulative drift
const VERTEX_SHADER = `
attribute vec2 a_position;
attribute float a_size;
attribute float a_alpha;
attribute float a_sway;

uniform vec2 u_resolution;
uniform float u_time;
uniform float u_dpr;

varying float v_alpha;

void main() {
    // Add individual sway based on particle's unique sway factor (gentle sinusoidal movement)
    float sway = sin(u_time * 0.002 + a_sway * 6.28) * 8.0;
    
    // Position already includes wind drift from CPU update
    float x = a_position.x + sway;
    float y = a_position.y;
    
    vec2 clipSpace = (vec2(x, y) / u_resolution) * 2.0 - 1.0;
    clipSpace.y = -clipSpace.y;
    
    gl_Position = vec4(clipSpace, 0.0, 1.0);
    gl_PointSize = a_size * u_dpr;
    
    v_alpha = a_alpha;
}
`;

const FRAGMENT_SHADER = `
precision mediump float;

uniform vec4 u_color;

varying float v_alpha;

void main() {
    vec2 center = gl_PointCoord - vec2(0.5);
    float dist = length(center);
    
    float softness = 0.5;
    float alpha = 1.0 - smoothstep(0.3, softness, dist);
    
    float glow = exp(-dist * 3.0) * 0.3;
    alpha = alpha + glow;
    
    if (alpha < 0.01) {
        discard;
    }
    
    gl_FragColor = vec4(
        u_color.rgb,
        u_color.a * alpha * v_alpha
    );
}
`;

// Particle data structure offsets (for particleData - includes speed for physics)
const PARTICLE_STRIDE = 6; // x, y, speed, size, sway, alpha
const OFFSET_X = 0;
const OFFSET_Y = 1;
const OFFSET_SPEED = 2;
const OFFSET_SIZE = 3;
const OFFSET_SWAY = 4;
const OFFSET_ALPHA = 5;

// Interleaved buffer layout for GPU upload: [x, y, size, alpha, sway] per particle
const INTERLEAVED_STRIDE = 5; // floats per particle for GPU
const INTERLEAVED_BYTES = INTERLEAVED_STRIDE * 4; // 20 bytes per particle

export class SnowEffect implements BaseEffect {
  private gl: WebGLRenderingContext | null = null;
  private accumulationCtx: CanvasRenderingContext2D | null = null;
  private config: AtmosphereConfig | null = null;

  // WebGL resources
  private program: WebGLProgram | null = null;
  
  // Zero-allocation interleaved buffer for GPU upload
  private interleavedBuffer: WebGLBuffer | null = null;
  private interleavedData: Float32Array | null = null;

  // Shader locations
  private locations: {
    a_position: number;
    a_size: number;
    a_alpha: number;
    a_sway: number;
    u_resolution: WebGLUniformLocation | null;
    u_time: WebGLUniformLocation | null;
    u_dpr: WebGLUniformLocation | null;
    u_color: WebGLUniformLocation | null;
  } | null = null;

  // Accumulation smoothing throttle (run ~2x per second for softer results)
  private smoothingFrameCounter: number = 0;
  private smoothingInterval: number = 30; // Every 30 frames (~2x per second at 60fps)

  // Particle data using TypedArrays
  private particleData: Float32Array | null = null;
  private particleCount: number = 0;

  // Canvas dimensions
  private width: number = 0;
  private height: number = 0;
  private dpr: number = 1;

  // Accumulation height-map with A/B double buffering for zero-allocation smoothing
  private accumulationMap: Float32Array | null = null;
  private accumulationMapB: Float32Array | null = null;
  private useAccumulationBufferA: boolean = true;
  private accumulationResolution: number = 2; // Pixels per accumulation segment

  // Fade state for smooth muting
  private globalAlphaMultiplier: number = 1.0;
  private isFading: boolean = false;
  private fadeStartTime: number = 0;
  private fadeDuration: number = 1500; // 1.5 seconds
  private onFadeComplete: (() => void) | null = null;

  // Deterministic ground rendering throttle (replaces Math.random())
  private groundRenderFrameCount: number = 0;

  initialize(
    gl: WebGLRenderingContext,
    accumulationCtx: CanvasRenderingContext2D,
    config: AtmosphereConfig
  ): void {
    this.gl = gl;
    this.accumulationCtx = accumulationCtx;
    this.config = config;

    // Enable blending for transparency
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);

    // Create shader program
    this.program = this.createProgram(gl, VERTEX_SHADER, FRAGMENT_SHADER);
    if (!this.program) {
      throw new Error('Failed to create snow shader program');
    }

    // Get attribute and uniform locations
    this.locations = {
      a_position: gl.getAttribLocation(this.program, 'a_position'),
      a_size: gl.getAttribLocation(this.program, 'a_size'),
      a_alpha: gl.getAttribLocation(this.program, 'a_alpha'),
      a_sway: gl.getAttribLocation(this.program, 'a_sway'),
      u_resolution: gl.getUniformLocation(this.program, 'u_resolution'),
      u_time: gl.getUniformLocation(this.program, 'u_time'),
      u_dpr: gl.getUniformLocation(this.program, 'u_dpr'),
      u_color: gl.getUniformLocation(this.program, 'u_color'),
    };

    // Create single interleaved buffer for zero-allocation rendering
    this.interleavedBuffer = gl.createBuffer();

    // Initialize particles
    this.initializeParticles();
    
    // Force initialize accumulation map
    this.initializeAccumulation();
  }

  /**
   * Create and compile shader program
   */
  private createProgram(
    gl: WebGLRenderingContext,
    vertexSource: string,
    fragmentSource: string
  ): WebGLProgram | null {
    const vertexShader = this.createShader(gl, gl.VERTEX_SHADER, vertexSource);
    const fragmentShader = this.createShader(gl, gl.FRAGMENT_SHADER, fragmentSource);

    if (!vertexShader || !fragmentShader) {
      return null;
    }

    const program = gl.createProgram();
    if (!program) return null;

    gl.attachShader(program, vertexShader);
    gl.attachShader(program, fragmentShader);
    gl.linkProgram(program);

    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      console.error('Program link failed:', gl.getProgramInfoLog(program));
      gl.deleteProgram(program);
      return null;
    }

    // Clean up shaders (they're now part of the program)
    gl.deleteShader(vertexShader);
    gl.deleteShader(fragmentShader);

    return program;
  }

  /**
   * Create and compile a shader
   */
  private createShader(
    gl: WebGLRenderingContext,
    type: number,
    source: string
  ): WebGLShader | null {
    const shader = gl.createShader(type);
    if (!shader) return null;

    gl.shaderSource(shader, source);
    gl.compileShader(shader);

    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
      console.error('Shader compile failed:', gl.getShaderInfoLog(shader));
      gl.deleteShader(shader);
      return null;
    }

    return shader;
  }

  /**
   * Initialize particle data
   */
  private initializeParticles(): void {
    if (!this.config) return;

    const baseCount = this.config.particleCount;
    const intensity = this.config.snow.intensity;
    this.particleCount = Math.floor(baseCount * intensity);

    // Create particle data array (for physics simulation)
    this.particleData = new Float32Array(this.particleCount * PARTICLE_STRIDE);
    
    // Pre-allocate interleaved buffer for GPU upload (zero-allocation rendering)
    this.interleavedData = new Float32Array(this.particleCount * INTERLEAVED_STRIDE);

    const { min: minSpeed, max: maxSpeed } = this.config.snow.speed;
    const { min: minSize, max: maxSize } = this.config.snow.size;

    for (let i = 0; i < this.particleCount; i++) {
      const offset = i * PARTICLE_STRIDE;

      // Random initial position
      this.particleData[offset + OFFSET_X] = Math.random() * this.width;
      this.particleData[offset + OFFSET_Y] = Math.random() * this.height;

      // Random speed
      this.particleData[offset + OFFSET_SPEED] =
        minSpeed + Math.random() * (maxSpeed - minSpeed);

      // Random size
      this.particleData[offset + OFFSET_SIZE] =
        minSize + Math.random() * (maxSize - minSize);

      // Random sway factor
      this.particleData[offset + OFFSET_SWAY] = Math.random();

      // Random initial alpha (0.6 - 1.0)
      this.particleData[offset + OFFSET_ALPHA] = 0.6 + Math.random() * 0.4;
    }
  }

  /**
   * Initialize accumulation height-map with A/B double buffering
   */
  private initializeAccumulation(): void {
    const segments = Math.ceil(this.width / this.accumulationResolution);
    this.accumulationMap = new Float32Array(segments);
    this.accumulationMapB = new Float32Array(segments);
    this.useAccumulationBufferA = true;
  }

  onResize(width: number, height: number, dpr: number): void {
    this.width = width;
    this.height = height;
    this.dpr = dpr;

    // Reinitialize particles with new dimensions
    if (this.config) {
      this.initializeParticles();
    }

    // Reinitialize accumulation map
    this.initializeAccumulation();
  }

  onConfigChange(config: AtmosphereConfig): void {
    this.config = config;

    // Reinitialize particles if count changed significantly
    const newCount = Math.floor(config.particleCount * config.snow.intensity);
    if (Math.abs(newCount - this.particleCount) > 50) {
      this.initializeParticles();
    }
  }

  /**
   * Start fading out the snow effect
   * @param duration - Fade duration in milliseconds (default: 1500ms)
   * @param onComplete - Callback when fade completes
   */
  fadeOut(duration: number = 1500, onComplete?: () => void): void {
    if (this.isFading) return;
    
    this.isFading = true;
    this.fadeDuration = duration;
    this.fadeStartTime = performance.now();
    this.onFadeComplete = onComplete || null;
    
    console.log('[Atmosphere] Starting fade out animation...');
  }

  /**
   * Check if currently fading
   */
  getIsFading(): boolean {
    return this.isFading;
  }

  /**
   * Reset fade state (for restarting the effect)
   */
  resetFade(): void {
    this.globalAlphaMultiplier = 1.0;
    this.isFading = false;
    this.fadeStartTime = 0;
    this.onFadeComplete = null;
  }

  update(deltaTime: number, _currentTime: number): void {
    if (!this.particleData || !this.config || !this.config.snow.enabled) return;

    // Handle fade animation
    if (this.isFading) {
      const elapsed = performance.now() - this.fadeStartTime;
      const progress = Math.min(elapsed / this.fadeDuration, 1.0);
      
      // Use easeOutQuad for smooth deceleration
      this.globalAlphaMultiplier = 1.0 - (progress * progress);
      
      // Also reduce accumulation during fade
      if (this.accumulationMap) {
        const fadeRate = 0.02 * deltaTime * 60; // Accelerated melt during fade
        for (let i = 0; i < this.accumulationMap.length; i++) {
          this.accumulationMap[i] = Math.max(0, this.accumulationMap[i] * (1 - fadeRate));
        }
      }
      
      if (progress >= 1.0) {
        this.isFading = false;
        this.globalAlphaMultiplier = 0;
        console.log('[Atmosphere] Fade out complete');
        
        if (this.onFadeComplete) {
          this.onFadeComplete();
          this.onFadeComplete = null;
        }
        return;
      }
    }

    const { maxHeight, rate } = this.config.snow.accumulation;
    const accumulationEnabled = this.config.snow.accumulation.enabled;
    
    // Get wind strength for CPU-side horizontal drift
    const windEnabled = this.config.snow.wind.enabled;
    const windStrength = windEnabled ? this.config.snow.wind.strength : 0;
    // Scale wind: strength of 5.0 = ~3 pixels per frame at 60fps
    const windDrift = windStrength * 0.6 * deltaTime * 60;

    // Early-exit threshold: only check collision for particles near the ground
    // This skips expensive height-map lookups for 80-90% of particles
    const groundThreshold = this.height - maxHeight - 20;

    for (let i = 0; i < this.particleCount; i++) {
      const offset = i * PARTICLE_STRIDE;

      // Get particle properties
      const speed = this.particleData[offset + OFFSET_SPEED];
      const size = this.particleData[offset + OFFSET_SIZE];

      // Update Y position (falling)
      this.particleData[offset + OFFSET_Y] += speed * 60 * deltaTime;
      
      // Apply directional wind to X position (cumulative drift)
      // This creates realistic wind effect where snow drifts sideways as it falls
      if (windDrift !== 0) {
        this.particleData[offset + OFFSET_X] += windDrift;
      }

      // Get current position
      const x = this.particleData[offset + OFFSET_X];
      const y = this.particleData[offset + OFFSET_Y];

      // Early-exit: only perform expensive collision detection for particles near ground
      if (y > groundThreshold) {
        // Check for ground collision (with accumulation)
        let groundLevel = this.height;
        
        if (accumulationEnabled && this.accumulationMap) {
          const segmentIndex = Math.floor(x / this.accumulationResolution);
          if (segmentIndex >= 0 && segmentIndex < this.accumulationMap.length) {
            groundLevel = this.height - this.accumulationMap[segmentIndex];
          }
        }

        // Reset particle when it hits ground or goes off-screen
        if (y >= groundLevel) {
          // Add to accumulation using bell-curve distribution
          // Distributes height to neighbors for softer, more natural dunes
          if (accumulationEnabled && this.accumulationMap) {
            const segmentIndex = Math.floor(x / this.accumulationResolution);
            const deposit = rate * size;
            // Bell-curve weights: center=40%, ±1=24% each, ±2=12% each (total 100%)
            const weights = [0.12, 0.24, 0.40, 0.24, 0.12];
            for (let w = 0; w < 5; w++) {
              const idx = segmentIndex - 2 + w;
              if (idx >= 0 && idx < this.accumulationMap.length) {
                this.accumulationMap[idx] = Math.min(
                  this.accumulationMap[idx] + deposit * weights[w],
                  maxHeight
                );
              }
            }
          }

          // Reset particle to top
          this.particleData[offset + OFFSET_X] = Math.random() * this.width;
          this.particleData[offset + OFFSET_Y] = -10 - Math.random() * 50;
          this.particleData[offset + OFFSET_SPEED] =
            this.config.snow.speed.min +
            Math.random() * (this.config.snow.speed.max - this.config.snow.speed.min);
        }
      }

      // Wrap horizontally with buffer
      if (x < -50) {
        this.particleData[offset + OFFSET_X] = this.width + 50;
      } else if (x > this.width + 50) {
        this.particleData[offset + OFFSET_X] = -50;
      }
    }

    // Apply snow melting
    if (accumulationEnabled && this.accumulationMap && this.config.snow.accumulation.meltRate > 0) {
      const meltAmount = this.config.snow.accumulation.meltRate * deltaTime;
      for (let i = 0; i < this.accumulationMap.length; i++) {
        this.accumulationMap[i] = Math.max(0, this.accumulationMap[i] - meltAmount);
      }
    }

    // Smooth accumulation map (throttled to ~1x per second, preserves height)
    // === ZERO-ALLOCATION: A/B double buffering instead of new array ===
    this.smoothingFrameCounter++;
    if (accumulationEnabled && this.accumulationMap && this.accumulationMapB 
        && this.accumulationMap.length > 2 
        && this.smoothingFrameCounter >= this.smoothingInterval) {
      this.smoothingFrameCounter = 0;
      
      // Select source and destination buffers (ping-pong)
      const src = this.useAccumulationBufferA ? this.accumulationMap : this.accumulationMapB;
      const dst = this.useAccumulationBufferA ? this.accumulationMapB : this.accumulationMap;
      
      // 5-neighbor box blur for soft, rounded dunes instead of spikes
      // Uses wider kernel for more aggressive smoothing
      for (let i = 0; i < src.length; i++) {
        const current = src[i];
        const prev = i > 0 ? src[i - 1] : src[i];
        const prev2 = i > 1 ? src[i - 2] : prev;
        const next = i < src.length - 1 ? src[i + 1] : src[i];
        const next2 = i < src.length - 2 ? src[i + 2] : next;
        // Equal-weight box blur across 5 neighbors creates soft hills
        dst[i] = (prev2 + prev + current + next + next2) / 5;
      }
      
      // Swap buffers (pointer swap, zero allocation)
      this.useAccumulationBufferA = !this.useAccumulationBufferA;
      this.accumulationMap = dst;
    }
  }

  render(): void {
    if (!this.gl || !this.program || !this.locations || !this.particleData || !this.interleavedData || !this.config) {
      return;
    }

    if (!this.config.snow.enabled) return;

    const gl = this.gl;
    const locs = this.locations;

    // Use shader program
    gl.useProgram(this.program);

    // Set uniforms (wind is now handled in CPU update)
    gl.uniform2f(locs.u_resolution, this.width, this.height);
    gl.uniform1f(locs.u_time, performance.now());
    gl.uniform1f(locs.u_dpr, this.dpr);

    // Set snow color with global alpha multiplier for fade effect
    const { r, g, b, alpha } = this.config.snow.color;
    gl.uniform4f(locs.u_color, r / 255, g / 255, b / 255, alpha * this.globalAlphaMultiplier);

    // === ZERO-ALLOCATION: Fill pre-allocated interleaved buffer ===
    // Layout: [x, y, size, alpha, sway] per particle (5 floats = 20 bytes)
    for (let i = 0; i < this.particleCount; i++) {
      const srcOffset = i * PARTICLE_STRIDE;
      const dstOffset = i * INTERLEAVED_STRIDE;
      
      this.interleavedData[dstOffset + 0] = this.particleData[srcOffset + OFFSET_X];
      this.interleavedData[dstOffset + 1] = this.particleData[srcOffset + OFFSET_Y];
      this.interleavedData[dstOffset + 2] = this.particleData[srcOffset + OFFSET_SIZE];
      this.interleavedData[dstOffset + 3] = this.particleData[srcOffset + OFFSET_ALPHA];
      this.interleavedData[dstOffset + 4] = this.particleData[srcOffset + OFFSET_SWAY];
    }

    // === SINGLE GPU UPLOAD: One bufferData call instead of 4 ===
    gl.bindBuffer(gl.ARRAY_BUFFER, this.interleavedBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, this.interleavedData, gl.DYNAMIC_DRAW);

    // === INTERLEAVED ATTRIBUTE POINTERS: Using stride and offset ===
    // Position: 2 floats at offset 0
    gl.enableVertexAttribArray(locs.a_position);
    gl.vertexAttribPointer(locs.a_position, 2, gl.FLOAT, false, INTERLEAVED_BYTES, 0);

    // Size: 1 float at offset 8 (after x, y)
    gl.enableVertexAttribArray(locs.a_size);
    gl.vertexAttribPointer(locs.a_size, 1, gl.FLOAT, false, INTERLEAVED_BYTES, 8);

    // Alpha: 1 float at offset 12 (after x, y, size)
    gl.enableVertexAttribArray(locs.a_alpha);
    gl.vertexAttribPointer(locs.a_alpha, 1, gl.FLOAT, false, INTERLEAVED_BYTES, 12);

    // Sway: 1 float at offset 16 (after x, y, size, alpha)
    gl.enableVertexAttribArray(locs.a_sway);
    gl.vertexAttribPointer(locs.a_sway, 1, gl.FLOAT, false, INTERLEAVED_BYTES, 16);

    // Draw particles
    gl.drawArrays(gl.POINTS, 0, this.particleCount);

    // Deterministic ground render throttling (replaces random for predictable frame timing)
    this.groundRenderFrameCount++;
    // Reset counter at 60000 to prevent overflow in long-running sessions
    if (this.groundRenderFrameCount >= 60000) {
      this.groundRenderFrameCount = 0;
    }
    const throttle = this.config.snow.accumulation.groundRenderThrottle || 12;
    if (this.groundRenderFrameCount % throttle === 0) {
      this.renderAccumulation();
    }
  }

  /**
   * Clear the accumulation canvas and optionally reset the height-map
   * Called when accumulation is toggled off or when a full reset is needed
   */
  clearAccumulation(resetHeightMap: boolean = false): void {
    // Clear the 2D canvas
    if (this.accumulationCtx) {
      const canvas = this.accumulationCtx.canvas;
      this.accumulationCtx.clearRect(0, 0, canvas.width, canvas.height);
    }
    
    // Optionally reset the height-map data
    if (resetHeightMap && this.accumulationMap) {
      this.accumulationMap.fill(0);
      if (this.accumulationMapB) {
        this.accumulationMapB.fill(0);
      }
    }
  }

  /**
   * Render snow accumulation using Canvas 2D
   */
  private renderAccumulation(): void {
    if (!this.accumulationCtx || !this.accumulationMap || !this.config) return;
    
    const ctx = this.accumulationCtx;
    const canvas = ctx.canvas;

    // Always clear the canvas first
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    // If accumulation is disabled, just clear and return (no rendering)
    if (!this.config.snow.accumulation.enabled) return;

    // Check if there's any accumulation (lowered threshold for earlier visibility)
    let hasAccumulation = false;
    for (let i = 0; i < this.accumulationMap.length; i++) {
      if (this.accumulationMap[i] > 0.1) {
        hasAccumulation = true;
        break;
      }
    }

    if (!hasAccumulation) return;

    // Get color from config
    const { r, g, b } = this.config.snow.color;

    // Draw snow ground using bezier curves for smooth appearance
    ctx.beginPath();
    ctx.moveTo(0, this.height);

    // Start from left edge
    const firstHeight = this.accumulationMap[0] || 0;
    ctx.lineTo(0, this.height - firstHeight);

    // Draw smooth curve through accumulation points using larger stride
    // Stride of 2 creates softer, more rounded "dune" shapes
    const stride = 2;
    for (let i = 0; i < this.accumulationMap.length; i += stride) {
      const x = i * this.accumulationResolution;
      const h = this.accumulationMap[i];
      const y = this.height - h;

      if (i === 0) {
        ctx.lineTo(x, y);
      } else if (i >= stride * 2) {
        // Use bezierCurveTo for smoother curves with better control points
        const prevIdx = Math.max(0, i - stride);
        const prevPrevIdx = Math.max(0, i - stride * 2);
        
        const prevX = prevIdx * this.accumulationResolution;
        const prevH = this.accumulationMap[prevIdx];
        const prevY = this.height - prevH;
        
        const prevPrevX = prevPrevIdx * this.accumulationResolution;
        const prevPrevH = this.accumulationMap[prevPrevIdx];
        const prevPrevY = this.height - prevPrevH;
        
        // Control points: blend between previous points for smooth curve
        const cp1x = prevPrevX + (prevX - prevPrevX) * 0.5;
        const cp1y = prevPrevY + (prevY - prevPrevY) * 0.5;
        const cp2x = prevX + (x - prevX) * 0.5;
        const cp2y = prevY + (y - prevY) * 0.5;
        
        ctx.bezierCurveTo(cp1x, cp1y, cp2x, cp2y, x, y);
      } else {
        // First few points use quadratic for smooth start
        const prevIdx = Math.max(0, i - stride);
        const prevX = prevIdx * this.accumulationResolution;
        const prevH = this.accumulationMap[prevIdx];
        const prevY = this.height - prevH;
        const midX = (prevX + x) / 2;
        const midY = (prevY + y) / 2;
        ctx.quadraticCurveTo(prevX, prevY, midX, midY);
      }
    }

    // Complete the path
    const lastX = (this.accumulationMap.length - 1) * this.accumulationResolution;
    const lastH = this.accumulationMap[this.accumulationMap.length - 1] || 0;
    ctx.lineTo(lastX, this.height - lastH);
    ctx.lineTo(this.width, this.height - lastH);
    ctx.lineTo(this.width, this.height);
    ctx.closePath();

    // Create gradient for snow ground with config color and alpha multiplier for fading
    const alphaMultiplier = this.globalAlphaMultiplier;
    const gradient = ctx.createLinearGradient(0, this.height - 60, 0, this.height);
    
    // Lighter version for top (add brightness)
    const lightR = Math.min(255, r + 20);
    const lightG = Math.min(255, g + 15);
    const lightB = Math.min(255, b + 10);
    
    // Slightly blue-tinted shadow for depth
    const shadowR = Math.max(0, r - 30);
    const shadowG = Math.max(0, g - 15);
    const shadowB = Math.min(255, b + 5);
    
    gradient.addColorStop(0, `rgba(${lightR}, ${lightG}, ${lightB}, ${0.95 * alphaMultiplier})`);
    gradient.addColorStop(0.3, `rgba(${r}, ${g}, ${b}, ${0.9 * alphaMultiplier})`);
    gradient.addColorStop(1, `rgba(${shadowR}, ${shadowG}, ${shadowB}, ${0.85 * alphaMultiplier})`);

    ctx.fillStyle = gradient;
    ctx.fill();

    // Add subtle shadow/depth using a darker version of the base color
    ctx.beginPath();
    ctx.moveTo(0, this.height);
    ctx.lineTo(0, this.height - firstHeight + 2);

    // Use same stride as main curve for consistent rendering
    for (let i = 0; i < this.accumulationMap.length; i += stride) {
      const x = i * this.accumulationResolution;
      const h = this.accumulationMap[i];
      const y = this.height - h + 3;
      ctx.lineTo(x, y);
    }

    ctx.lineTo(this.width, this.height);
    ctx.closePath();

    // Shadow tint based on config color
    const depthR = Math.max(0, r - 55);
    const depthG = Math.max(0, g - 35);
    const depthB = Math.min(255, b + 15);
    ctx.fillStyle = `rgba(${depthR}, ${depthG}, ${depthB}, ${0.3 * alphaMultiplier})`;
    ctx.fill();
  }

  dispose(): void {
    if (!this.gl) return;

    const gl = this.gl;

    // Delete interleaved buffer (single buffer instead of 4)
    if (this.interleavedBuffer) gl.deleteBuffer(this.interleavedBuffer);

    // Delete program
    if (this.program) gl.deleteProgram(this.program);

    // Clear arrays (particle data, interleaved GPU data, and accumulation double-buffers)
    this.particleData = null;
    this.interleavedData = null;
    this.accumulationMap = null;
    this.accumulationMapB = null;

    // Clear references
    this.gl = null;
    this.accumulationCtx = null;
    this.program = null;
    this.locations = null;
    this.interleavedBuffer = null;
  }
}

